import React, { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';
import ReportWidget from './components/ReportWidget';
import { fetchDowntimeBreakdown } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const MAX_DISPLAY_ASSETS = 15;

const CustomTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        fontSize: '0.85rem',
      }}
    >
      <div className="fw-bold mb-2">{dataPoint.fullName}</div>
      <div className="d-flex align-items-center gap-2 mb-1">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('reporting.downtime_breakdown.planned_downtime', 'Planned Downtime')}</span>
        <span className="fw-bold ms-auto">{dataPoint.plannedPercentage}%</span>
      </div>
      <div className="d-flex align-items-center gap-2 mb-1">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '4px',
            backgroundColor: '#ef4444',
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('shared.unplanned', 'Unplanned')}</span>
        <span className="fw-bold ms-auto">{Math.round(dataPoint.unplanned)}h</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '4px',
            backgroundColor: '#3b82f6',
            display: 'inline-block',
          }}
        />
        <span className="text-muted">{t('shared.planned', 'Planned')}</span>
        <span className="fw-bold ms-auto">{Math.round(dataPoint.planned)}h</span>
      </div>
    </div>
  );
};

/**
 * Self-fetching by default. When {@code data} is passed externally the
 * widget skips its Redux dispatch and renders the supplied payload —
 * used by the Global Dashboard to embed the same JSX cross-org.
 */
const DowntimeBreakdownWidget = ({
  organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
  data: externalData, loading: externalLoading, error: externalError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    downtimeBreakdown: reduxData,
    downtimeBreakdownLoading: reduxLoading,
    downtimeBreakdownError: reduxError,
  } = useSelector(state => state.Reporting);
  const externalMode = externalData !== undefined;
  const data = externalMode ? externalData : reduxData;
  const loading = externalMode ? !!externalLoading : reduxLoading;
  const error = externalMode ? externalError : reduxError;

  // Fetch data when deps change (skipped in external mode).
  useEffect(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchDowntimeBreakdown(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [externalMode, dispatch, organizationId, dateRange?.startDate, dateRange?.endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const handleRefresh = useCallback(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchDowntimeBreakdown(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [externalMode, dispatch, organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data?.assets) return [];
    return data.assets.slice(0, MAX_DISPLAY_ASSETS).map(asset => ({
      name: asset.assetName && asset.assetName.length > 25
        ? asset.assetName.substring(0, 25) + '...'
        : asset.assetName || t('shared.unknown', 'Unknown'),
      fullName: asset.assetName || t('shared.unknown', 'Unknown'),
      unplanned: asset.unplannedDowntimeHours,
      planned: asset.plannedDowntimeHours,
      plannedPercentage: asset.plannedDowntimePercentage,
    }));
  }, [data?.assets, t]);

  const displayedCount = chartData.length;
  const totalAssets = data?.totalAssets || 0;
  const isEmpty = !data || !data.assets || data.assets.length === 0;
  const hasActiveFilters = criticality || locationId || vendorId;

  const chartHeight = Math.max(250, displayedCount * 45);

  return (
    <ReportWidget
      title={
        <span className="d-flex align-items-center gap-2">
          {t('reporting.downtime_breakdown.title', 'Unplanned vs Planned Downtime')}
          <Info
            size={15}
            className="text-muted"
            id="downtimeBreakdownTooltip"
            style={{ cursor: 'help' }}
          />
          <UncontrolledTooltip placement="top" target="downtimeBreakdownTooltip">
            {t('reporting.downtime_breakdown.tooltip', 'Shows how much downtime is unplanned vs. planned per asset. Helps measure preventive maintenance effectiveness and identify assets needing more proactive work.')}
          </UncontrolledTooltip>
        </span>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={hasActiveFilters
        ? t('shared.no_assets_found_matching_the_selected_fi', 'No assets found matching the selected filters.')
        : t('reporting.downtime_breakdown.empty', 'No assets with downtime in the selected period.')}
      onRefresh={externalMode ? undefined : handleRefresh}
    >
      {/* Chart */}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis type="number" label={{ value: 'H', position: 'insideRight', offset: -5 }} />
          <YAxis
            dataKey="name"
            type="category"
            width={200}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar
            dataKey="unplanned"
            stackId="downtime"
            fill="#ef4444"
            name={t('shared.unplanned', 'Unplanned')}
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="planned"
            stackId="downtime"
            fill="#3b82f6"
            name={t('shared.planned', 'Planned')}
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Footer */}
      <div className="text-muted mt-3" style={{ fontSize: '0.8rem' }}>
        {t('reporting.downtime_breakdown.assets_with_downtime', '{{count}} asset with downtime', { count: totalAssets })}
        {totalAssets !== 1 ? 's' : ''}
        {totalAssets > MAX_DISPLAY_ASSETS && ` (${t('reporting.downtime_breakdown.showing_top', 'showing top {{count}}', { count: MAX_DISPLAY_ASSETS })})`}
      </div>
    </ReportWidget>
  );
};

export default DowntimeBreakdownWidget;
